import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { arSA, enUS } from '@mui/material/locale';
import '../styles/compact-mode.css';

export interface UserSettings {
  // إعدادات الإشعارات
  emailNotifications: boolean;
  pushNotifications: boolean;
  maintenanceNotifications: boolean;
  salesNotifications: boolean;
  
  // إعدادات المظهر
  theme: 'light' | 'dark';
  language: 'ar' | 'en';
  compactMode: boolean;
  
  // إعدادات الخصوصية
  showEmail: boolean;
  showPhone: boolean;
  allowMessages: boolean;
  
  // معلومات الحساب
  name: string;
  email: string;
  status: 'active' | 'inactive';
}

interface UserSettingsContextType {
  settings: UserSettings;
  updateSetting: (key: keyof UserSettings, value: any) => void;
  resetSettings: () => void;
  saveSettings: () => void;
}

const defaultSettings: UserSettings = {
  // إعدادات الإشعارات
  emailNotifications: true,
  pushNotifications: true,
  maintenanceNotifications: true,
  salesNotifications: false,
  
  // إعدادات المظهر
  theme: 'light',
  language: 'ar',
  compactMode: false,
  
  // إعدادات الخصوصية
  showEmail: false,
  showPhone: false,
  allowMessages: true,
  
  // معلومات الحساب
  name: 'أحمد السواح',
  email: 'baballouk@gmail.com',
  status: 'active'
};

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (context === undefined) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
};

interface UserSettingsProviderProps {
  children: ReactNode;
}

export const UserSettingsProvider: React.FC<UserSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<UserSettings>(defaultSettings);

  // تحميل الإعدادات من localStorage عند بدء التشغيل
  useEffect(() => {
    const savedSettings = localStorage.getItem('userAccountSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings({ ...defaultSettings, ...parsedSettings });
      } catch (error) {
        console.error('Error loading saved settings:', error);
      }
    }
  }, []);

  // تحديث إعداد معين
  const updateSetting = (key: keyof UserSettings, value: any) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      // حفظ تلقائي في localStorage
      localStorage.setItem('userAccountSettings', JSON.stringify(newSettings));
      return newSettings;
    });
  };

  // إعادة تعيين الإعدادات
  const resetSettings = () => {
    setSettings(defaultSettings);
    localStorage.setItem('userAccountSettings', JSON.stringify(defaultSettings));
  };

  // حفظ الإعدادات
  const saveSettings = () => {
    localStorage.setItem('userAccountSettings', JSON.stringify(settings));
  };

  // إنشاء Theme بناءً على الإعدادات
  const theme = createTheme(
    {
      palette: {
        mode: settings.theme,
        primary: {
          main: '#1976d2',
        },
        secondary: {
          main: '#dc004e',
        },
        ...(settings.theme === 'dark' && {
          background: {
            default: '#121212',
            paper: '#1e1e1e',
          },
        }),
      },
      direction: settings.language === 'ar' ? 'rtl' : 'ltr',
      typography: {
        fontFamily: settings.language === 'ar' 
          ? '"Cairo", "Roboto", "Helvetica", "Arial", sans-serif'
          : '"Roboto", "Helvetica", "Arial", sans-serif',
        ...(settings.compactMode && {
          fontSize: 12,
          h1: { fontSize: '1.8rem' },
          h2: { fontSize: '1.6rem' },
          h3: { fontSize: '1.4rem' },
          h4: { fontSize: '1.2rem' },
          h5: { fontSize: '1.1rem' },
          h6: { fontSize: '1rem' },
        }),
      },
      spacing: settings.compactMode ? 6 : 8,
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              ...(settings.compactMode && {
                padding: '4px 8px',
                fontSize: '0.75rem',
              }),
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              ...(settings.compactMode && {
                padding: '8px',
              }),
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              ...(settings.compactMode && {
                minHeight: '48px',
              }),
            },
          },
        },
      },
    },
    settings.language === 'ar' ? arSA : enUS
  );

  // تطبيق اتجاه النص على body
  useEffect(() => {
    document.body.dir = settings.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = settings.language;
  }, [settings.language]);

  // تطبيق الوضع المضغوط على body
  useEffect(() => {
    if (settings.compactMode) {
      document.body.classList.add('compact-mode');
    } else {
      document.body.classList.remove('compact-mode');
    }
  }, [settings.compactMode]);

  const contextValue: UserSettingsContextType = {
    settings,
    updateSetting,
    resetSettings,
    saveSettings,
  };

  return (
    <UserSettingsContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </UserSettingsContext.Provider>
  );
};

export default UserSettingsProvider;
